function sleepWithSignal(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error("aborted");
      error.code = "aborted";
      reject(error);
      return;
    }
    const timer = setTimeout(() => {signal?.removeEventListener('abort', onAbort); resolve();}, ms);
    const onAbort = () => {
      clearTimeout(timer);
      const error = new Error("aborted");
      error.code = "aborted";
      reject(error);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function defaultIsPaid(entitlement) {
  if (!entitlement) return false;
  const plan = String(entitlement.plan || "").toLowerCase();
  const access = String(entitlement.accessState || "").toLowerCase();
  return (plan === "pro" || plan === "elite") && (access === "active" || access === "grace");
}

export async function pollEntitlementUntilPaid(options) {
  const pollBudgetMs = options.pollBudgetMs ?? 45_000;
  const sleep = options.sleep || ((ms, signal) => sleepWithSignal(ms, signal));
  const now = options.now || (() => Date.now());
  const isPaid = options.isPaidAccess || defaultIsPaid;
  const signal = options.signal;
  const isCurrentSession = options.isCurrentSession || (() => true);

  const deadline = now() + pollBudgetMs;
  let attempt = 0;
  let lastEntitlement = null;
  let sawUnpaid = false;
  let observedTransition = false;

  while (now() < deadline) {
    if (signal?.aborted || !isCurrentSession()) {
      return { status: "aborted", entitlement: lastEntitlement, attempts: attempt, observedTransition };
    }
    attempt += 1;
    try {
      const result = await options.fetchEntitlement({ signal });
      lastEntitlement = result?.entitlement || null;
      if (options.onTick) {
        options.onTick({ attempt, entitlement: lastEntitlement });
      }
      if (signal?.aborted || !isCurrentSession()) {
        return {status:'aborted',entitlement:null,attempts:attempt,observedTransition:false};
      }
      if (isPaid(lastEntitlement) && (!options.targetPlan || lastEntitlement?.plan === options.targetPlan)) {
        if (sawUnpaid) observedTransition = true;
        return {
          status: "paid",
          entitlement: lastEntitlement,
          attempts: attempt,
          observedTransition,
        };
      }
      sawUnpaid = true;
    } catch (error) {
      if (error?.code === "aborted" || error?.name === "AbortError" || !isCurrentSession()) {
        return { status: "aborted", entitlement: lastEntitlement, attempts: attempt, observedTransition, error };
      }
      if (options.onTick) {
        options.onTick({ attempt, entitlement: lastEntitlement, error });
      }
      if (error?.code === "signed_out" || error?.status === 401) {
        return { status: "signed_out", entitlement: lastEntitlement, attempts: attempt, error, observedTransition };
      }
    }

    const delay = Math.min(1500 * 2 ** Math.min(attempt - 1, 4), 8000);
    if (now() + delay >= deadline) break;
    try {
      await sleep(delay, signal);
    } catch (error) {
      return { status: "aborted", entitlement: lastEntitlement, attempts: attempt, observedTransition, error };
    }
  }

  return { status: "processing", entitlement: lastEntitlement, attempts: attempt, observedTransition };
}

export function readUntrustedCheckoutQuery(search) {
  const params = new URLSearchParams(search || "");
  const sessionId =
    params.get("session_id") || params.get("CHECKOUT_SESSION_ID") || params.get("checkout_session_id") || "";
  const displaySessionId = sessionId ? `${String(sessionId).slice(0, 12)}…` : null;
  return {
    displaySessionId,
    rawParamCount: [...params.keys()].length,
  };
}
